<template>
  <tile class="opc-addon-config">
    <foldable v-model="isExpanded" :arrow="hasSelectedPlan" :edit="hasSelectedPlan" :title="title">
      <template #head>
      <div class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img :src="logo" class="opc-zm-base-card__logo" :alt="title + ' Icon'" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i v-if="hasSelectedPlan && !isAdd2CartOrEditFlow" check-icon class="zm-icon-success main-icon" aria-hidden="true" />
          </h2>
          <div v-if="!isExpanded && hasSelectedPlan" class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset">
            <ul>
              <li>{{ getPlanCollapseText() }}</li>
            </ul>
          </div>
        </div>
      </div>
      </template>
      <div class="opc-addon-config__main">
        <p v-if="isAdd2CartFlow || isEditFlow" class="opc-addon-config__main__title" @click="onTipsLinkClick"
          v-html="$t('billing.translation_caption.subtitle')" v-modal-pop-link></p>
        <p v-else class="opc-addon-config__main__title">{{ configDescription }}</p>
        <div class="opc-addon-config__main__config">
          <div class="opc-addon-config-row__input__number">
            <input-number-with-popover :modelValue="license" ref="userInput" @update:modelValue="changeLicense" size="lg"
              controls-position="both" :min="minHosts" :max="maxHosts"
              :decrease-tips="decreaseTips" :label="title + ' Select the number of licenses'"
              :data-testid="`checkout-input-license-${selectedPlanCode}`"
            />
            <span class="opc-addon-config-row__input__number--unit">{{ unitWithPlural }}</span>
          </div>
          <bought-license :type="planTypes.TRANSLATION_CAPTIONS" />
          <zm-banner show-close v-if="mx_downSellLicenseErrorMessage" class="opc-common-info-alert" type="warning"
            show-icon>
            <p v-html="mx_downSellLicenseErrorMessage"></p>
          </zm-banner>
          <div class="opc-addon-config__main__title">
            <p class="subscription-cycle__title">{{$t('billing.select_billing_cycle') }}</p>
            <billed-billing-cycle-radio
              v-model="selectedBillingCycle"
              :billingCycleList="billingCycleOptions"
              :unit="unit"
              @change="onPlanSelect"
            />
          </div>
        </div>
      </div>
      <div v-if="isShowRemoveBtn" class="opc-zm-base-card__main__footer">
        <div class="opc-addon-config__btn--wrap">
          <zm-button data-testid="checkout-button-80" type="link" ghost @click="removePlanByKey" :aria-label="'remove ' + title + ' plan'">{{
            $t('billing.common.remove') }}</zm-button>
        </div>
      </div>
      <preview-payment class="opc-addon-config__preview-payment" />
    </foldable>
  </tile>
</template>

<script>
import plansMixin from '@/plans/mx/plan';
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import {
  planTypes,
  BILLING_CYCLE,
  DEFAULT_MAX_HOST,
  DEFAULT_MIN_HOST,
} from '@/common/constants';
import { flowTypes, pageConfig } from '@/common/service/page-config.js';
import { get } from 'lodash';
import AddonMixin from '../../mx/addon';
import BilledBillingCycleRadio from "@/components/service/config-billing-cycle/billed-cycle/index.vue";
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import autofill from "@/plans/mx/autofill";
import baseplanFreeToPaidReplenishMixin from "@/components/capacity/mx/freetrial-baseplan-replenish";
import { getPlanUnitByQuantity, planUnitMap } from '@/common/product-unit';
import { getPlanCollapseText } from '@/common/product';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: 'Translation-Config',
  key: planTypes[planTypes.TRANSLATION_CAPTIONS],
  mixins: [
    plansMixin,
    AddonMixin,
    autofill,
    pageConfig.type === flowTypes.add2cart ? baseplanFreeToPaidReplenishMixin : null,
  ].filter(Boolean),
  components: { PreviewPayment, Tile, Foldable, InputNumberWithPopover, BilledBillingCycleRadio, BoughtLicense},
  data() {
    return {
      isExpanded: true,
      selectedPlanCode: '',
      selectedBillingCycle: '',
      license: 1,
      title: this.$t('billing.translation_caption.title'),
      planType: planTypes.TRANSLATION_CAPTIONS,
      previousQuantity: 1,
      configDescription: this.$t('billing.translation_caption.config_desc'),
      planTypes,
    };
  },
  computed: {
    ...mapGetters(['getSelectedPlans', 'getCurrentBasePlanInfo', 'basicConfig', 'currentEditPlan', 'supportAutoAddAnotherBasePlan', 'maxCycleValueBasePlan']),
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
    }),
    logo() {
      return this.isAdd2CartFlow || this.isEditFlow ? require('@/assets/plans/translated_captions_add.svg') : require('@/assets/plans/translated_captions.svg')
    },
    isAdd2CartFlow() {
      return this.$context.flow === flowTypes.add2cart && this.$context.is.addPage;
    },
    isEditFlow() {
      return this.$context.flow === flowTypes.edit && this.$context.is.editPage;
    },
    hasSelectedPlan() {
      return !!this.selectedPlanCode;
    },
    minHosts() {
      if (this.isEditFlow && (this.basicConfig.downSellData.disableDownSell || !this.isStandardPrice)) {
        return this.previousQuantity;
      }
      return DEFAULT_MIN_HOST;
    },
    maxHosts() {
      return DEFAULT_MAX_HOST;
    },
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.planType);
    },
    getUnit() {
      const units = {
        [BILLING_CYCLE.MONTHLY]: this.$t('billing.common.per_billing_cycle_license.monthly'),
        [BILLING_CYCLE.ANNUAL]: this.$t('billing.common.per_billing_cycle_license.annual'),
        [BILLING_CYCLE.QUARTERLY]: this.$t('billing.common.per_billing_cycle_license.quarterly'),
        [BILLING_CYCLE.TWO_YEARS]: this.$t('billing.common.per_billing_cycle_license.two_years'),
        [BILLING_CYCLE.THREE_YEARS]: this.$t('billing.common.per_billing_cycle_license.three_years'),
      }
      return (cycle) => units[cycle];
    },
    unit() {
      return this.planList[0].unit;
    },
    unitWithPlural() {
      // Use getPlanUnitByQuantity to get proper singular/plural form based on license count
      return getPlanUnitByQuantity(planUnitMap.license, this.license);
    },
    billingCycleOptions() {
      const currentBasePlanCycleValue = this.maxCycleValueBasePlan?.cycleValue || 0;

      const basePlanName = this.$t(this.maxCycleValueBasePlan?.typeNameKey || '');
      const addonPlanName = this.$t(this.title);

      return this.planList.map(plan => {
        let isDisabledExceedCycle = plan.cycleValue > currentBasePlanCycleValue && get(this.currentEditPlan, ['billing_cycle'], '') !== plan.billing_cycle;
        if (this.supportAutoAddAnotherBasePlan) {
          isDisabledExceedCycle = false;
        }
        const isBulkLicenseAccountDisabled = this.isEditFlow && this.basicConfig.isBulkLicenseAccount && get(this.currentEditPlan, ['billing_cycle'], '') !== plan.billing_cycle;
        const disabled = isDisabledExceedCycle || isBulkLicenseAccountDisabled;
        const disabledTooltip = isBulkLicenseAccountDisabled ? this.$t('billing.error.buld_acount_license') :
          isDisabledExceedCycle ? this.$t('billing.planform.annual_addon_disable_tooltip', [basePlanName, addonPlanName, basePlanName]) : '';

        return {plan, disabled, disabledTooltip};
      }).reverse();
    },
    trackingPlanKey() {
      return getTrackingProductNameByPlanType(this.planType);
    },
    cartSelectedPlan() {
      return get(this.getSelectedPlans.filter((p) => p.type === this.planType), [0]);
    },
    decreaseTips() {
      const downSellData = this.basicConfig.downSellData;
      if (this.$context.is.edit && this.license === this.previousQuantity) {
        if (downSellData.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }

        return this.forNonStandardPriceTips;
      }

      return '';
    },
  },
  mounted() {
    this.previousQuantity = this.currentEditPlan?.cartInfo?.selected_number ?? DEFAULT_MIN_HOST;
    // refresh page
    this.$nextTick(() => {
      this.mx_validDownSellLicense(this.license);
    });
    this.$context.$on('collapse-plan-card', this.collapseCard);
  },
  methods: {
    ...mapMutations(['updateSelectPlan', 'removePlan']),
    ...mapActions(['switchSelectPlan']),
    onTipsLinkClick(e) {
      if (e.target.tagName.toLowerCase() === 'a') {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`, 'view supported languages');
      }
    },
    onPlanSelect(oldPlan, newPlan) {
      const oldPlanCode = this.selectedPlanCode;
      const newPlanCode = newPlan?.plan_code;

      if (this.selectedPlanCode !== newPlanCode) {
        if (oldPlanCode) {
          this.switchSelectPlan([oldPlanCode, newPlanCode, undefined]);
        } else {
          let quantity = this.license;
          if (quantity) {
            quantity = quantity <= this.maxHosts ? quantity : this.maxHosts;
          } else {
            quantity = this.minHosts;
          }
          this.license = quantity;
          this.updateSelectPlan({
            plan_code: newPlanCode,
            cartInfo: { selected_number: quantity },
          });
        }
        this.selectedPlanCode = newPlanCode;
        this.selectedBillingCycle = newPlan.billing_cycle;
        // event tracking
        const billingCycle = this.cartSelectedPlan?.billing_cycle;
        billingCycle && this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    changeLicense(newLicense) {
      const planCode = this.selectedPlanCode;
      const oldLicense = this.license;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }
      this.mx_validDownSellLicense(newLicense);

      this.license = newLicense;
      // event tracking
      planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
        `${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    // For A11Y
    getFirstFocusableControl() {
      return Promise.resolve(this.$refs.userInput)
    },
    collapseCard() {
      this.isExpanded = false;
    },
    getPlanCollapseText() {
      if (!this.cartSelectedPlan) return '';
      const quantity = this.cartSelectedPlan?.cartInfo?.selected_number || 0;
      const unitDesc = getPlanUnitByQuantity(planUnitMap.license, quantity).toLowerCase();
      return getPlanCollapseText({
        amount: this.cartSelectedPlan?.amount,
        billingCycle: this.cartSelectedPlan?.billing_cycle,
        quantity: quantity,
        cycleValue: this.cartSelectedPlan?.cycleValue,
        unit: planUnitMap.license,
        customUnitDesc: unitDesc,
      });
    },
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan) {
        this.selectedPlanCode = plan?.plan_code ?? "";
        this.selectedBillingCycle = plan?.billing_cycle ?? "";
        this.license = plan?.cartInfo?.selected_number ?? this.minHosts;
      },
    },
    isExpanded: {
      handler(val) {
        this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} accordion`, val ? 'expand' : 'collapse');
      },
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/zoom-card';

.opc-addon-config-row {
  &__input__number {
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;

    &--unit {
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
      font-weight: 600;
    }
  }
}

.opc-addon-config {
  .subscription-cycle__title {
    font-weight: 600;
    font-size: 16px;
    line-height: 150%;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    margin-bottom: 8px;
  }

  .opc-addon-config__main {
    border-top: 1px solid #dfe3e8;

    &__title {
      margin-top: 16px;
      color: var(--zoom-color-text-stronger-neutral);
    }
  }

  &__preview-payment {
    margin-top: 24px;
  }

}
</style>
